import { HttpEventType } from '@angular/common/http';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FdesDialogDraft, FdesFormulaType, FdesGeneration, FdesSiteMode } from 'src/app/core/_models/fdes';
import { FdesDocumentKind, FdesService, fdesDocumentKind, fdesFallbackFileName, saveFdesFile } from 'src/app/core/_services/fdes.service';
import { UploadService } from 'src/app/core/_services/upload.service';
import { TranslateService } from '@ngx-translate/core';

// Chantier de référence des FDES mères (modèle de calcul, Param_Entrées ligne 72) : les enrobés à froid y sont
// transportés sur 45,7 km, les autres sur 28,4 km.
const DEFAULT_SITE_DISTANCE_KM = 28.4;
const DEFAULT_SITE_DISTANCE_COLD_MIX_KM = 45.7;
const COLD_MIX_TYPES = ['BBE', 'GE'];

@Component({
  selector: 'app-dialog-generate-fdes',
  templateUrl: './dialog-generate-fdes.component.html',
  styleUrls: ['./dialog-generate-fdes.component.scss']
})
export class DialogGenerateFdesComponent implements OnInit, OnDestroy {
  view: 'form' | 'loading' | 'success' = 'form';

  form: FormGroup;
  logoFileName = '';
  logoFile?: File;
  ftpFileName = '';
  ftpUploading = false;
  ftpError = false;

  formulaTypes: FdesFormulaType[] = [];
  history: FdesGeneration[] = [];
  errorMessage = '';
  warnings: string[] = [];
  // Les types de produit sont lus dans le classeur publié : si l'appel échoue, c'est qu'aucun modèle de
  // calcul n'est actif (ou que son fichier est absent du serveur) et la génération est impossible.
  blockingMessage = '';

  // FTP déposée pendant la session ; renvoyée à la formule à la fermeture pour synchroniser son justificatif.
  private uploadedProof?: { fileName: string; fileDescription: any };
  private siteChanges?: Subscription;

  constructor(
    public dialogRef: MatDialogRef<DialogGenerateFdesComponent>,
    private fdesService: FdesService,
    private uploadService: UploadService,
    private formBuilder: FormBuilder,
    @Inject(MAT_DIALOG_DATA)
    // `canGenerate` à faux = organisation qui n'est plus adhérente Routes de France, ou profil en lecture
    // seule : la popup ne sert alors qu'à retrouver et télécharger les FDES déjà produites (décision PO du
    // 2026-07-29).
    public data: {
      formulaId?: string;
      formulaName?: string;
      industryName?: string;
      proofFileName?: string;
      canGenerate?: boolean;
      ownerIsRdfAdherent?: boolean;
      needsDeclarantAddress?: boolean;
    },
    private translate: TranslateService
  ) {
    // Le logo et la FTP sont facultatifs. La FTP n'est pas un contrôle du formulaire : elle part au serveur
    // dès sa sélection, via le justificatif de formule (UploadProof).
    this.form = this.formBuilder.group({
      type: ['', Validators.required],
      thickness: [null, [Validators.required, Validators.min(0)]],
      site: ['default' as FdesSiteMode],
      location: ['', Validators.required],
      distance: [null, [Validators.required, Validators.min(0)]]
    });

    // Seule l'agence porte une adresse en base : quand la formule est scopée EU, ou que l'agence n'a
    // rien de saisi, l'adresse du déclarant est demandée ici plutôt que laissée vide dans la FDES.
    if (this.data?.needsDeclarantAddress) {
      this.form.addControl('declarantAddress', this.formBuilder.control('', Validators.required));
    }

    this.restoreDraft();
    this.applySiteMode();
    this.siteChanges = this.form.get('site')!.valueChanges.subscribe(() => this.applySiteMode(true));
    this.siteChanges.add(this.form.get('type')!.valueChanges.subscribe(() => this.applySiteMode()));
  }

  ngOnInit(): void {
    this.loadFormulaTypes();
    this.loadHistory();
  }

  // Les types proposés dépendent de la famille de la formule : le serveur ne renvoie que ceux qu'elle peut
  // déclarer, un enrobé à froid n'ayant que la grave-émulsion et le béton bitumineux à l'émulsion.
  private loadFormulaTypes(): void {
    const formulaId = this.data?.formulaId;
    if (!formulaId) return;

    this.fdesService.getFormulaTypes(formulaId).subscribe({
      next: (types) => {
        this.formulaTypes = types || [];
        // La liste des types évolue avec les mises à jour du classeur : un type restauré qui n'existe
        // plus laisserait le champ vide à l'écran mais rempli dans le formulaire.
        if (this.form.value.type && !this.formulaTypes.some((t) => t.code === this.form.value.type)) {
          this.form.get('type')?.setValue('');
        }
      },
      error: (err) => (this.blockingMessage = this.extractError(err))
    });
  }

  ngOnDestroy(): void {
    this.siteChanges?.unsubscribe();
    const formulaId = this.data?.formulaId;
    if (!formulaId) return;
    // Une FDES produite clôt la saisie : la conserver ferait repartir la génération suivante sur les
    // valeurs de la précédente. Tout autre motif de fermeture — erreur de génération, annulation pour
    // aller corriger la formule — garde la saisie pour la réouverture.
    if (this.view === 'success') {
      this.fdesService.clearDraft(formulaId);
      return;
    }
    const v = this.form.getRawValue();
    this.fdesService.saveDraft(formulaId, {
      type: v.type,
      thickness: v.thickness,
      site: v.site,
      location: v.location,
      distance: v.distance,
      declarantAddress: v.declarantAddress,
      logoFile: this.logoFile,
      logoFileName: this.logoFileName
    });
  }

  get formCrls() {
    return this.form.controls;
  }

  get isComplete(): boolean {
    return this.form.valid;
  }

  get isDefaultSite(): boolean {
    return this.form.get('site')?.value === 'default';
  }

  // Chantier par défaut : la distance est celle des FDES mères et la localisation n'a pas lieu d'être.
  // Revenir à un chantier spécifique vide les deux champs plutôt que d'y laisser la distance de référence.
  private applySiteMode(siteChanged = false): void {
    const location = this.form.get('location')!;
    const distance = this.form.get('distance')!;
    if (this.isDefaultSite) {
      location.setValue('', { emitEvent: false });
      location.disable({ emitEvent: false });
      distance.setValue(this.defaultSiteDistance(this.form.get('type')!.value), { emitEvent: false });
      distance.disable({ emitEvent: false });
      return;
    }
    if (siteChanged) distance.setValue(null, { emitEvent: false });
    location.enable({ emitEvent: false });
    distance.enable({ emitEvent: false });
  }

  private defaultSiteDistance(type: string): number | null {
    if (!type) return null;
    return COLD_MIX_TYPES.includes(type) ? DEFAULT_SITE_DISTANCE_COLD_MIX_KM : DEFAULT_SITE_DISTANCE_KM;
  }

  // Absent = comportement historique, on peut générer. Une organisation qui a perdu son adhésion, comme un
  // profil de bureau d'études, ouvre la popup en lecture seule ; seule la première l'annonce.
  get canGenerate(): boolean {
    return this.data?.canGenerate !== false;
  }

  get isNotAdherent(): boolean {
    return !this.canGenerate && this.data?.ownerIsRdfAdherent === false;
  }

  get subtitle(): string {
    const f = this.data?.formulaName || "Formule d'enrobé";
    const i = this.data?.industryName;
    return i ? `${f} — ${i}` : f;
  }

  onLogoSelected(files: FileList | null): void {
    const file = files?.item(0);
    if (!file) return;
    this.logoFile = file;
    this.logoFileName = file.name;
  }

  // Même mécanisme que le bouton « Ajouter un justificatif » de la page formule : endpoint UploadProof,
  // fichier nommé {resourceId}.{ext}. Un seul justificatif par formule, donc l'upload remplace l'existant.
  onFtpSelected(files: FileList | null): void {
    const file = files?.item(0);
    if (!file || !this.data?.formulaId) return;
    this.ftpError = false;
    this.ftpUploading = true;
    const formData = new FormData();
    formData.append('file', file, `${this.data.formulaId}.${file.name.split('.').pop()}`);
    this.uploadService.uploadProof(this.data.formulaId, formData).subscribe({
      next: (event: any) => {
        if (event.type === HttpEventType.Response) {
          this.ftpUploading = false;
          this.ftpFileName = file.name;
          this.uploadedProof = { fileName: file.name, fileDescription: event.body };
        }
      },
      error: () => {
        this.ftpUploading = false;
        this.ftpError = true;
      }
    });
  }

  generate(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    if (!this.data?.formulaId) return;
    const v = this.form.getRawValue();
    this.errorMessage = '';
    this.view = 'loading';
    this.readLogoBase64().then((logoBase64) => {
      this.fdesService
        .generate(this.data.formulaId!, {
          productType: v.type,
          thicknessCm: v.thickness,
          siteDistanceKm: v.distance,
          siteLocation: v.location || undefined,
          declarantAddress: v.declarantAddress || undefined,
          logoFileName: this.logoFile?.name,
          logoBase64
        })
        .subscribe({
          next: (generation) => {
            this.warnings = generation?.warnings || [];
            this.view = 'success';
            this.loadHistory();
            this.downloadAll(generation);
          },
          error: (err) => {
            this.errorMessage = this.extractError(err);
            this.view = 'form';
          }
        });
    });
  }

  download(generation: FdesGeneration, kind: FdesDocumentKind): void {
    this.downloadInSequence(generation, [kind]);
  }

  // « La FDES générée se télécharge sur le PC » : les documents produits partent dès qu'ils existent —
  // la déclaration (le PDF tant qu'il est là, le Word sinon), le classeur de résultats, le XML de dépôt
  // INIES et l'attestation de vérification.
  private downloadAll(generation: FdesGeneration): void {
    const kinds: FdesDocumentKind[] = [];
    const document = fdesDocumentKind(generation);
    if (document) kinds.push(document);
    if (generation.excelRelativePath) kinds.push('excel');
    if (generation.xmlRelativePath) kinds.push('xml');
    if (generation.attestationRelativePath) kinds.push('attestation');

    this.downloadInSequence(generation, kinds);
  }

  // En file plutôt qu'en parallèle : le dossier de téléchargements garde cet ordre (la déclaration, les
  // données, le XML) et on ne charge pas trois blobs à la fois. Un échec n'emporte pas les suivants,
  // chaque document se suffit à lui-même.
  private downloadInSequence(generation: FdesGeneration, kinds: FdesDocumentKind[]): void {
    const [kind, ...rest] = kinds;
    if (!kind || !this.data?.formulaId) return;

    this.fdesService.downloadFile(this.data.formulaId, generation.fdesGenerationId, kind).subscribe({
      next: (response) => {
        saveFdesFile(response, fdesFallbackFileName(kind, this.data.formulaName, generation.generatedDate));
        this.downloadInSequence(generation, rest);
      },
      error: (err) => {
        this.errorMessage = this.extractError(err);
        this.downloadInSequence(generation, rest);
      }
    });
  }

  close(): void {
    this.dialogRef.close(this.uploadedProof);
  }

  private restoreDraft(): void {
    const draft: FdesDialogDraft | undefined = this.data?.formulaId ? this.fdesService.getDraft(this.data.formulaId) : undefined;
    if (!draft) return;
    this.form.patchValue(draft);
    this.logoFile = draft.logoFile;
    this.logoFileName = draft.logoFileName || '';
  }

  // Le logo part en base64 dans le corps JSON de la génération : le serveur le reçoit, aucun stockage
  // pour l'instant (pas d'endpoint multipart à créer pour une donnée qui n'a pas encore de destination).
  private readLogoBase64(): Promise<string | undefined> {
    if (!this.logoFile) return Promise.resolve(undefined);
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result).split(',')[1]);
      reader.onerror = () => resolve(undefined);
      reader.readAsDataURL(this.logoFile!);
    });
  }

  private loadHistory(): void {
    if (!this.data?.formulaId) return;
    this.fdesService.getHistory(this.data.formulaId).subscribe((rows) => (this.history = rows || []));
  }

  // Le classeur peut refuser plusieurs contrôles à la fois : chaque motif tient sa ligne, ponctuée, car
  // les libellés viennent du classeur et n'ont pas tous leur point final.
  get errorLines(): string[] {
    return this.splitMessage(this.errorMessage);
  }

  get blockingLines(): string[] {
    return this.splitMessage(this.blockingMessage);
  }

  private splitMessage(message: string): string[] {
    return (message || '')
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => (/[.!?:…]$/.test(line) ? line : `${line}.`));
  }

  // 400 FluentValidation : un tableau de { propertyName, errorMessage }. 500 et 503 : un message simple.
  // ErrorIntercept ayant déjà réduit la réponse à une chaîne, c'est ce cas qui arrive en pratique.
  private extractError(err: any): string {
    if (typeof err === 'string' && err) return err;
    const body = err?.error;
    if (Array.isArray(body) && body.length) {
      const messages = body.map((e: any) => e?.errorMessage || e?.ErrorMessage).filter(Boolean);
      return messages.length ? messages.join('\n') : this.translate.instant('fdes_generation_failed');
    }
    if (typeof body === 'string') return body;
    return body?.title || err?.message || this.translate.instant('fdes_generation_failed');
  }
}
